import { useLocalObservable } from 'mobx-react-lite';
import { KeyboardEvent, ClipboardEvent } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';

import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps } from '@gem/popups';

import { ReminderType } from 'APP/model/calendar/calendar.types';
import { getHumanizeRRule, getUnitLabel } from 'APP/utils/calendar/getHumanizeRRule';

import { UnitEnum } from '../../Form.types';
import { TypeMap } from './CustomReminderPopup.constants';
import { IFormData } from './CustomReminderPopup.types';

export interface ICustomReminderPopupParams {
  onSubmit(values: any, params?: any): void;
}

interface ICustomReminderPopupPresenter {
  initialValues: IFormData;
  typeItems: { key: string; title: string }[];
  getUnitItems(time: number): { key: string; title: string }[];
  onSubmit(data: IFormData): Promise<void>;
  onClose(): void;
  handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void;
  handlePaste(e: ClipboardEvent<HTMLInputElement>): void;
}

interface ICustomReminderPopupReturnType {
  presenter: ICustomReminderPopupPresenter;
  form: UseFormReturn<IFormData>;
}

export function useCustomReminderPopupPresenter({
  popupInstance,
  onClose,
  params,
}: ICommonPopupsProps<ICustomReminderPopupParams>): ICustomReminderPopupReturnType {
  const { t } = useTranslation();
  const presenter = useLocalObservable<ICustomReminderPopupPresenter>(() => ({
    initialValues: {
      time: 30,
      unit: UnitEnum.Minutes,
      type: `${ReminderType.EMAIL}`,
    },

    handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
      const invalidChars = ['-', '+', 'e', 'E', '.', ','];
      if (invalidChars.includes(e.key)) {
        e.preventDefault();
      }
    },

    handlePaste(e: ClipboardEvent<HTMLInputElement>): void {
      const paste = e.clipboardData.getData('text');
      if (/[^0-9]/.test(paste)) {
        e.preventDefault();
      }
    },

    async onSubmit(values: IFormData): Promise<void> {
      params?.onSubmit({
        key: values,
        title: getHumanizeRRule(values.unit),
      });

      popupInstance.close();
    },

    onClose(): void {
      popupInstance.close();
      onClose?.();
    },

    getUnitItems(time: number): { key: string; title: string }[] {
      return Object.entries({
        [`${UnitEnum.Minutes}`]: getUnitLabel(time, 'minute'),
        [`${UnitEnum.Hours}`]: getUnitLabel(time, 'hour'),
        [`${UnitEnum.Days}`]: getUnitLabel(time, 'day'),
        [`${UnitEnum.Weeks}`]: getUnitLabel(time, 'week'),
      }).map(([key, value]) => ({
        key,
        title: value,
      }));
    },

    get typeItems(): { key: string; title: string }[] {
      return Object.entries(TypeMap).map(([key, value]) => ({
        key,
        title: t(value),
      }));
    },
  }));

  const form = useForm<IFormData>({
    mode: 'onChange',
    defaultValues: presenter.initialValues,
  });

  return { presenter, form };
}
